// pages/EventDetailPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';
import { Clock, Lock, Globe, ArrowLeft, Key, Copy, Check, Edit, Trash2, X, Image as ImageIcon } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';

const API_BASE = API_BASE_URL;

interface Team {
  _id: string;
  name: string;
  description?: string;
  members: { _id: string; name: string; email: string }[];
  creator: { _id: string; name: string; email: string };
}

interface EventData {
  _id: string;
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social' | 'conference' | 'training' | 'webinar' | 'networking' | 'other';
  color: string;
  isPublic: boolean;
  image?: string;
  images?: string[];
  creator?: { _id: string; name: string; email: string };
  teams: Team[];
  accessCode?: string;
}

const EVENT_TYPES = ['meeting', 'workshop', 'deadline', 'social', 'conference', 'training', 'webinar', 'networking', 'other'] as const;
const EVENT_TYPE_LABELS: Record<string, string> = {
  meeting: 'Meeting', workshop: 'Workshop', deadline: 'Deadline', social: 'Social Event',
  conference: 'Conference', training: 'Training', webinar: 'Webinar', networking: 'Networking', other: 'Other',
};
const EVENT_TYPE_COLORS: Record<string, string> = {
  meeting: '#3788d8', workshop: '#56cc9d', deadline: '#ff9f40', social: '#10b981',
  conference: '#0ea5e9', training: '#14b8a6', webinar: '#f59e0b', networking: '#ec4899', other: '#64748b',
};

function getEventColor(event: EventData): string {
  return event.color || EVENT_TYPE_COLORS[event.type] || EVENT_TYPE_COLORS.meeting;
}

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => selectUser(state));
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [formData, setFormData] = useState({ title: '', description: '', start: '', end: '', type: 'meeting', isPublic: true });

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/events/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: { _: Date.now() }
        });
        setEvent(res.data.data);
      } catch (err: any) {
        let errorMessage = 'Unable to load event';
        if (err.response?.status === 404) errorMessage = 'Event not found';
        else if (err.response?.status === 401) errorMessage = 'Unauthorized to view this event';
        else if (err.response?.status === 403) errorMessage = 'Access denied to this event';
        else if (err.response?.data?.error) errorMessage = err.response.data.error;
        else if (err.message) errorMessage = err.message;
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getTypeLabel = (type: string) => EVENT_TYPE_LABELS[type] || type;

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      meeting: 'bg-blue-100 text-blue-700', workshop: 'bg-emerald-100 text-emerald-700',
      deadline: 'bg-orange-100 text-orange-700', social: 'bg-emerald-100 text-emerald-700',
      conference: 'bg-sky-100 text-sky-700', training: 'bg-teal-100 text-teal-700',
      webinar: 'bg-amber-100 text-amber-700', networking: 'bg-pink-100 text-pink-700', other: 'bg-slate-100 text-slate-700',
    };
    return colors[type] || colors.other;
  };

  const isCreator = event?.creator?._id === user?._id;

  const handleCopyCode = () => {
    if (event?.accessCode) {
      navigator.clipboard.writeText(event.accessCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEditClick = () => {
    if (!event) return;
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    setFormData({
      title: event.title,
      description: event.description || '',
      start: new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16),
      end: new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16),
      type: event.type,
      isPublic: event.isPublic
    });
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setShowEditModal(true);
    setError(null);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f => ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(f.type) && f.size <= 5 * 1024 * 1024);
    if (files.length > 0) {
      setPhotoFiles(prev => [...prev, ...files]);
      setError(null);
      files.forEach(f => {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreviews(prev => [...prev, reader.result as string]);
        reader.readAsDataURL(f);
      });
    }
    (e.target as HTMLInputElement).value = '';
  };

  const handleRemovePhoto = (index: number) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'isPublic' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || !user?._id) return;
    if (!formData.start || !formData.end) { setError('Start and end dates are required'); return; }
    const startDate = new Date(formData.start);
    const endDate = new Date(formData.end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) { setError('Invalid date format'); return; }
    if (endDate <= startDate) { setError('End date must be after start date'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (photoFiles.length > 0) {
        const fd = new FormData();
        photoFiles.forEach(f => fd.append('images', f));
        await axios.post(`${API_BASE}/events/${event._id}/upload-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
      }
      const payload = { title: formData.title.trim(), description: (formData.description || '').trim(), start: startDate.toISOString(), end: endDate.toISOString(), type: formData.type, isPublic: Boolean(formData.isPublic), creatorId: user._id };
      const res = await axios.put(`${API_BASE}/events/${event._id}`, payload, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      if (res.data.success) {
        setEvent({ ...res.data.data, images: event.images, image: event.image });
        setShowEditModal(false);
        setError(null);
      } else {
        setError(res.data.error || 'Error updating event');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Error updating event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!event || !user?._id) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/events/${event._id}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: { creatorId: user._id }
      });
      navigate(-1);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Error deleting event');
      setShowDeleteConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center min-h-screen items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg">
          <p className="text-red-600 font-semibold">{error || 'Event not found'}</p>
          <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 mb-6 transition">
          <ArrowLeft className="h-5 w-5" />Back
        </button>

        <div className="bg-white rounded-2xl shadow-lg mb-8 border border-gray-200 overflow-hidden" style={{ borderLeftWidth: '4px', borderLeftColor: getEventColor(event) }}>
          {(event.images?.length ? event.images : event.image ? [event.image] : []).length > 0 && (
            <div className="aspect-video w-full overflow-hidden bg-gray-100 relative">
              {event.images?.length ? (
                <div className="flex overflow-x-auto snap-x snap-mandatory h-full">
                  {event.images.map((src, i) => (
                    <img key={i} src={getImageUrl(src) || ''} alt={`${event.title} ${i + 1}`} className="min-w-full w-full h-full object-cover snap-center flex-shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  ))}
                </div>
              ) : (
                <img src={getImageUrl(event.image || '') || ''} alt={event.title} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
            </div>
          )}
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{event.title}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getTypeColor(event.type)}`}>{getTypeLabel(event.type)}</span>
                  {event.isPublic ? (
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700"><Globe className="h-4 w-4" />Public</span>
                  ) : (
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700"><Lock className="h-4 w-4" />Private</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full shrink-0" style={{ backgroundColor: getEventColor(event) }} title="Event color" />
                {isCreator && (
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={handleEditClick} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"><Edit className="h-4 w-4" />Edit</button>
                    <button onClick={() => setShowDeleteConfirm(true)} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"><Trash2 className="h-4 w-4" />Delete</button>
                  </div>
                )}
              </div>
            </div>

            {event.description && <p className="text-gray-600 mb-6 leading-relaxed">{event.description}</p>}

            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="h-5 w-5 text-emerald-600" />
                <div><span className="font-medium">Start: </span>{formatDate(event.start)}</div>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="h-5 w-5 text-blue-600" />
                <div><span className="font-medium">End: </span>{formatDate(event.end)}</div>
              </div>
            </div>

            {event.creator && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white font-bold flex items-center justify-center text-sm">
                    {event.creator.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Created by</p>
                    <p className="font-semibold text-gray-900">{event.creator.name || 'Unknown'}</p>
                    {event.creator.email && <p className="text-xs text-gray-500">{event.creator.email}</p>}
                  </div>
                </div>

                {isCreator && !event.isPublic && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border-2 border-emerald-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="h-5 w-5 text-emerald-600" />
                      <h3 className="font-semibold text-gray-900 text-sm">Access Code</h3>
                    </div>
                    {event.accessCode ? (
                      <div className="flex items-center gap-2">
                        <div onClick={handleCopyCode} className="flex-1 px-4 py-2 bg-white rounded-lg border-2 border-emerald-300 cursor-pointer hover:border-emerald-400 hover:bg-emerald-50">
                          <code className="text-lg font-mono font-bold text-emerald-700 tracking-wider block text-center">{event.accessCode}</code>
                        </div>
                        <button onClick={handleCopyCode} className="p-2 rounded-lg hover:bg-emerald-100" title="Copy code">
                          {copied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5 text-emerald-600" />}
                        </button>
                      </div>
                    ) : (
                      <div className="px-4 py-2 bg-white rounded-lg border-2 border-yellow-300">
                        <p className="text-xs text-yellow-800 text-center">Generating code...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h3 className="text-2xl font-bold text-gray-900">Edit Event</h3>
                <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5 text-gray-500" /></button>
              </div>
              <form onSubmit={handleUpdateEvent} className="p-6 space-y-6">
                {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg"><p className="text-sm text-red-600">{error}</p></div>}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input name="title" required value={formData.title} onChange={handleFormChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" placeholder="Event title" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea name="description" value={formData.description} onChange={handleFormChange} rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" placeholder="Event description" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start date and time *</label>
                    <input type="datetime-local" name="start" required value={formData.start} onChange={handleFormChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End date and time *</label>
                    <input type="datetime-local" name="end" required value={formData.end} onChange={handleFormChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
                  <div className="space-y-3">
                    {(event.images?.length ? event.images : event.image ? [event.image] : []).length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {(event.images?.length ? event.images : [event.image!]).map((src, i) => (
                          <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-300">
                            <img src={getImageUrl(src) || ''} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                    {photoPreviews.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {photoPreviews.map((src, i) => (
                          <div key={`new-${i}`} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-300">
                            <img src={src} alt="" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => handleRemovePhoto(i)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <input type="file" id="edit-event-photos" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" multiple onChange={handlePhotoChange} className="hidden" />
                    <label htmlFor="edit-event-photos" className="cursor-pointer inline-flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-400 hover:bg-emerald-50">
                      <ImageIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Add Photos</span>
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                    <select name="type" required value={formData.type} onChange={handleFormChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                      {EVENT_TYPES.map(type => <option key={type} value={type}>{getTypeLabel(type)}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center pt-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" name="isPublic" checked={formData.isPublic} onChange={handleFormChange} className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" />
                      <span className="text-sm font-medium text-gray-700">Public event</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
                  <button type="button" onClick={() => setShowEditModal(false)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={submitting}>Cancel</button>
                  <button type="submit" className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Delete Event</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this event? This action cannot be undone.</p>
                {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4"><p className="text-sm text-red-600">{error}</p></div>}
                <div className="flex items-center justify-end gap-4">
                  <button onClick={() => { setShowDeleteConfirm(false); setError(null); }} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={submitting}>Cancel</button>
                  <button onClick={handleDeleteEvent} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50" disabled={submitting}>{submitting ? 'Deleting...' : 'Delete'}</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailPage;
